import { downloadBlob, downloadText, EXAMPLE_SAMPLE_ID, templateColumns, templateCsv } from './sheet'
import { writeXlsx } from './xlsx'
import { aiApplies, isTumourNormal } from './workflow'

// ----------------------------------------------------------------------
// The sample sheet template, as Excel or CSV. Both have the same columns, which
// follow the batch (templateColumns), and one row per sample in the panel with the
// values it has so far, or an example row when the panel is empty. The Excel file
// adds dropdowns for sex and AI, a frozen header, and a Help sheet.
// ----------------------------------------------------------------------

export const TEMPLATE_NAME = 'genesus_sample_sheet'
export const HELP_SHEET = 'Help'
const HELP_HEADER = ['Column', 'What to write']

const SEX_VALUES = ['Female', 'Male', 'Unknown']
const YES_NO = ['Yes', 'No']

// Width in Excel characters, text format for cells Excel would otherwise turn into
// numbers or dates, dropdowns, and the one-line help.
export const TEMPLATE_COLUMN_INFO = {
  sample_id: {
    width: 22,
    text: true,
    help: 'Required. The sample name from its files (FASTQ, BAM or VCF). Rows match samples by it.',
  },
  sex: {
    width: 12,
    list: { values: SEX_VALUES, error: 'Pick Female, Male or Unknown. A karyotype such as XXY also works.' },
    help: 'Female, Male or Unknown. A karyotype such as XXY also works.',
  },
  age: { width: 8, help: 'Age in years. Decimals are fine: 34.5 or 34,5.' },
  onset_age: { width: 11, help: 'Age in years when the symptoms started.' },
  ai: {
    width: 8,
    list: { values: YES_NO, error: 'Pick Yes or No, or leave it blank to follow the batch.' },
    help: 'Yes or No. AI adds credits per sample and needs HPO terms and clinical history. Blank follows the batch.',
  },
  hpo: { width: 30, text: true, help: 'HPO IDs separated by semicolons, for example HP:0001250; HP:0001263.' },
  clinical_history: { width: 48, text: true, help: 'Free text: symptoms, their course, family history.' },
  notes: { width: 30, text: true, help: 'Free text, kept with the sample.' },
  tumour_type: { width: 18, text: true, help: 'OncoTree code (LUAD) or name (Lung Adenocarcinoma).' },
  normal_sample: { width: 22, text: true, help: 'The sample_id of the matched normal sample.' },
}

/** The template's columns for a workflow and the samples in the panel. */
export const templateColumnsFor = (workflow = {}, samples = []) =>
  templateColumns({
    analysis: workflow.analysis === 'somatic' ? 'somatic' : 'germline',
    tumourNormal: isTumourNormal(workflow),
    ai: workflow.analysis !== 'somatic' && aiApplies({ ...workflow, analysis: 'germline' }, samples),
  })

const SEX_WORD = { XX: 'Female', XY: 'Male' }

const aiWord = (sample) => {
  if (sample.ai === true) return 'Yes'
  if (sample.ai === false || (sample.ai === undefined && sample.excludeAI)) return 'No'
  return ''
}

const numberOrBlank = (value) => (value === '' || value === null || value === undefined ? '' : value)

/** One sample's value for a template column: what the panel has for it so far. */
export const templateValue = (column, sample = {}) => {
  switch (column) {
    case 'sample_id':
      return sample.key || ''
    case 'sex':
      return SEX_WORD[sample.sex] || sample.sex || ''
    case 'age':
      return numberOrBlank(sample.age)
    case 'onset_age':
      return numberOrBlank(sample.onsetAge)
    case 'ai':
      return aiWord(sample)
    case 'hpo':
      return (sample.hpo || [])
        .map((item) => item.id)
        .filter(Boolean)
        .join('; ')
    case 'clinical_history':
      return sample.clinicalHistory || ''
    case 'notes':
      return sample.notes || ''
    case 'tumour_type':
      return sample.tumourType?.oncotree_code || sample.tumourType?.code || ''
    case 'normal_sample':
      return sample.normalKey || ''
    default:
      return ''
  }
}

const EXAMPLE = {
  sample_id: EXAMPLE_SAMPLE_ID,
  sex: 'Female',
  age: 34,
  onset_age: 12,
  ai: 'Yes',
  hpo: 'HP:0001250; HP:0001263',
  clinical_history: 'Seizures since age 12, developmental delay.',
  notes: 'Example row. It is skipped on import.',
  tumour_type: 'LUAD',
  normal_sample: 'EXAMPLE-01-N',
}

/** Rows of values in column order: one per sample, or the example row. */
export const templateRows = (columns, samples = []) =>
  samples.length
    ? samples.map((sample) => columns.map((column) => templateValue(column, sample)))
    : [columns.map((column) => EXAMPLE[column] ?? '')]

export const HELP_ROWS = (columns) => [
  ...columns.map((column) => [column, TEMPLATE_COLUMN_INFO[column]?.help || '']),
  [],
  ['', 'One row per sample. Only sample_id is required, and blank cells change nothing.'],
]

/** True for the Help sheet of this template, which an import leaves out. */
export const isTemplateHelp = (sheet) =>
  sheet?.name === HELP_SHEET && sheet.rows?.[0]?.[0] === HELP_HEADER[0] && sheet.rows?.[0]?.[1] === HELP_HEADER[1]

/** The template workbook: a Samples sheet and a Help sheet. */
export const templateWorkbook = (workflow, samples = []) => {
  const columns = templateColumnsFor(workflow, samples)
  return [
    {
      name: 'Samples',
      frozenHeader: true,
      validationRows: 200,
      columns: columns.map((column) => ({ header: column, ...TEMPLATE_COLUMN_INFO[column] })),
      rows: templateRows(columns, samples),
    },
    {
      name: HELP_SHEET,
      frozenHeader: true,
      columns: [
        { header: HELP_HEADER[0], width: 20 },
        { header: HELP_HEADER[1], width: 100 },
      ],
      rows: HELP_ROWS(columns),
    },
  ]
}

/**
 * Downloads the template for the batch: 'xlsx' (Excel, loaded on demand) or 'csv'.
 * @returns {Promise<void>} rejects when the Excel file could not be built
 */
export const downloadTemplate = async (format, samples = [], workflow = {}) => {
  if (format === 'xlsx') {
    const blob = await writeXlsx(templateWorkbook(workflow, samples))
    downloadBlob(`${TEMPLATE_NAME}.xlsx`, blob)
    return
  }
  const columns = templateColumnsFor(workflow, samples)
  downloadText(`${TEMPLATE_NAME}.csv`, templateCsv(columns, templateRows(columns, samples)))
}
